import { menuService } from '@/services/menuService';
import type { MenuItem } from '@/types/menu';
import type { ProductNavigationTree } from '@/types/navigationConfig';
import type { NavigationNode } from '@/types/navigationConfig';

interface FlattenedNavigationNode {
  id: string;
  label: string;
  route: string;
  itemType: 'group' | 'item';
  parentMenuItemId: string | null;
  sortOrder: number;
  depth: number;
  icon: string;
}

function toNodeType(itemType: MenuItem['itemType']): 'group' | 'item' {
  return itemType === 'group' ? 'group' : 'item';
}

function toMenuRoute(node: NavigationNode): string {
  if (node.type === 'group') {
    return '';
  }

  return node.route ?? '';
}

function sortNodes(nodes: NavigationNode[]): NavigationNode[] {
  return nodes
    .map((node) => ({
      ...node,
      children: sortNodes(node.children),
    }))
    .sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        left.id.localeCompare(right.id),
    );
}

function buildNavigationTree(items: MenuItem[]): NavigationNode[] {
  const nodeMap = new Map<string, NavigationNode>();
  const roots: NavigationNode[] = [];

  for (const item of items) {
    const type = toNodeType(item.itemType);
    nodeMap.set(item.id, {
      id: item.id,
      parentId: item.parentMenuItemId ?? null,
      label: item.label,
      icon: item.icon || (type === 'group' ? 'folder' : 'apps'),
      route: type === 'item' ? item.route : undefined,
      type,
      children: [],
      visibility: 'visible',
      displayOrder: item.sort_order ?? 0,
      openMode: 'sameTab',
    });
  }

  for (const item of items) {
    const node = nodeMap.get(item.id);
    if (!node) {
      continue;
    }

    const parentId = item.parentMenuItemId ?? null;
    const parent = parentId ? nodeMap.get(parentId) : undefined;

    if (parent && parent.type === 'group') {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return sortNodes(roots);
}

function flattenNodes(
  nodes: NavigationNode[],
  parentMenuItemId: string | null = null,
  depth = 0,
): FlattenedNavigationNode[] {
  return nodes.flatMap((node, index) => {
    const current: FlattenedNavigationNode = {
      id: node.id,
      label: node.label,
      route: toMenuRoute(node),
      itemType: node.type,
      parentMenuItemId,
      sortOrder: index,
      depth,
      icon: node.icon || (node.type === 'group' ? 'folder' : 'apps'),
    };

    return [current, ...flattenNodes(node.children, node.id, depth + 1)];
  });
}

function hasRemovedAncestor(
  itemId: string,
  removedIds: Set<string>,
  byId: Map<string, MenuItem>,
): boolean {
  let parentId = byId.get(itemId)?.parentMenuItemId ?? null;

  while (parentId) {
    if (removedIds.has(parentId)) {
      return true;
    }

    parentId = byId.get(parentId)?.parentMenuItemId ?? null;
  }

  return false;
}

function areNodesEquivalent(
  remote: MenuItem,
  desired: FlattenedNavigationNode,
): boolean {
  return (
    remote.label === desired.label &&
    (remote.route || '') === desired.route &&
    (remote.parentMenuItemId ?? null) === desired.parentMenuItemId &&
    (remote.itemType ?? 'item') === desired.itemType &&
    (remote.sort_order ?? 0) === desired.sortOrder &&
    (remote.icon || (desired.itemType === 'group' ? 'folder' : 'apps')) ===
      desired.icon
  );
}

export async function fetchProductNavigation(
  productId: string,
): Promise<ProductNavigationTree> {
  const items = await menuService.getMenus(productId, {
    includeHierarchy: true,
  });
  return {
    productId,
    nodes: buildNavigationTree(items),
    updatedAt: new Date().toISOString(),
  };
}

export async function saveProductNavigation(
  productId: string,
  nodes: NavigationNode[],
): Promise<ProductNavigationTree> {
  const remoteItems = await menuService.getMenus(productId, {
    includeHierarchy: true,
  });
  const desiredNodes = flattenNodes(nodes);

  const remoteById = new Map(remoteItems.map((item) => [item.id, item]));
  const desiredById = new Map(desiredNodes.map((node) => [node.id, node]));

  const removedIds = new Set(
    remoteItems.map((item) => item.id).filter((id) => !desiredById.has(id)),
  );
  const deleteRoots = [...removedIds].filter(
    (id) => !hasRemovedAncestor(id, removedIds, remoteById),
  );

  for (const deleteId of deleteRoots) {
    await menuService.deleteMenu(productId, deleteId);
  }

  const toCreate = desiredNodes.filter((node) => !remoteById.has(node.id));
  for (const node of toCreate) {
    await menuService.createMenu(productId, {
      id: node.id,
      label: node.label,
      route: node.route,
      parentMenuItemId: node.parentMenuItemId,
      itemType: node.itemType,
      sort_order: node.sortOrder,
      icon: node.icon,
    });
  }

  const toUpdate = desiredNodes.filter((node) => {
    const remote = remoteById.get(node.id);
    if (!remote) {
      return false;
    }

    return !areNodesEquivalent(remote, node);
  });

  for (const node of toUpdate) {
    await menuService.updateMenu(productId, node.id, {
      label: node.label,
      route: node.route,
      parentMenuItemId: node.parentMenuItemId,
      itemType: node.itemType,
      sort_order: node.sortOrder,
      icon: node.icon,
    });
  }

  return fetchProductNavigation(productId);
}

export async function createNavigationNode(): Promise<void> {
  return Promise.resolve();
}

export async function updateNavigationNodeApi(): Promise<void> {
  return Promise.resolve();
}

export async function deleteNavigationNodeApi(): Promise<void> {
  return Promise.resolve();
}

export async function fetchNavigationPermissions(): Promise<string[]> {
  return ['standard-user', 'admin', 'analyst', 'compliance-officer'];
}

export async function fetchNavigationIcons(): Promise<string[]> {
  return [
    'search',
    'notifications',
    'timeline',
    'description',
    'folder',
    'apps',
  ];
}

export async function fetchFeatureFlags(): Promise<string[]> {
  return [];
}
